/**
 * Jobs API calls.
 */

import client from './client';

export async function fetchJobQueue(params = {}) {
  const { data } = await client.get('/jobs/queue/', { params });
  return data.data;
}

export async function fetchJob(id) {
  const { data } = await client.get(`/jobs/${id}/`);
  return data.data;
}

export async function reorderQueue(items) {
  const { data } = await client.patch('/jobs/reorder/', items);
  return data.data;
}

export async function clockIn(jobId, { designPhaseId = null, laborType = 'standard', customStartTime = null } = {}) {
  const payload = { job_id: jobId, labor_type: laborType };
  if (designPhaseId) payload.design_phase_id = designPhaseId;
  if (customStartTime) payload.custom_start_time = customStartTime;
  const { data } = await client.post('/time-entries/clock-in/', payload);
  return data.data;
}

export async function clockOut(jobId, { notes = '', customEndTime = null } = {}) {
  const payload = { job_id: jobId };
  if (notes) payload.notes = notes;
  if (customEndTime) payload.custom_end_time = customEndTime;
  const { data } = await client.post('/time-entries/clock-out/', payload);
  return data.data;
}

export async function getActiveClock() {
  const { data } = await client.get('/time-entries/active/');
  return data.data;
}

export async function transitionJob(jobId, newStatus) {
  const { data } = await client.post(`/jobs/${jobId}/transition/`, { status: newStatus });
  return data.data;
}

export async function createJob(payload) {
  const { data } = await client.post('/jobs/', payload);
  return data.data;
}

export async function updateJob(jobId, payload) {
  const { data } = await client.patch(`/jobs/${jobId}/`, payload);
  return data.data;
}

export async function completeJob(jobId, payload = {}) {
  const { data } = await client.post(`/jobs/${jobId}/complete/`, payload);
  return data.data;
}

export async function shipJob(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/ship/`);
  return data.data;
}

export async function archiveJob(jobId, payload) {
  const { data } = await client.post(`/jobs/${jobId}/archive/`, payload);
  return data.data;
}

export async function finalizeAvailability(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/finalize-availability/`);
  return data.data;
}

export async function saveBomAsVersion(jobId, { mode = 'new_version', version_label = '', version_notes = '' } = {}) {
  const { data } = await client.post(`/jobs/${jobId}/save-bom-as-version/`, { mode, version_label, version_notes });
  return data.data;
}

export async function fetchArchivedJobs(params = {}) {
  const { data } = await client.get('/jobs/archived/', { params });
  return data;
}

export async function cancelJob(jobId, reason = '') {
  const { data } = await client.post(`/jobs/${jobId}/cancel/`, { reason });
  return data.data;
}

export async function fetchCancelledJobs(params = {}) {
  const { data } = await client.get('/jobs/cancelled/', { params });
  return data;
}

export async function fetchReadyToShipJobs(params = {}) {
  const { data } = await client.get('/jobs/ready-to-ship/', { params });
  return data;
}

export async function fetchShippedJobs(params = {}) {
  const { data } = await client.get('/jobs/shipped/', { params });
  return data;
}

export async function backfillJobStatuses() {
  const { data } = await client.post('/jobs/backfill-status/');
  return data.data;
}

export async function fetchCustomers(params = {}) {
  const { data } = await client.get('/customers/', { params: { page_size: 1000, ...params } });
  return data;
}

export async function fetchParentItems(params = {}) {
  const { data } = await client.get('/parent-items/', { params: { page_size: 500, ...params } });
  return data;
}

export async function fetchBomVersions(params = {}) {
  const { data } = await client.get('/parent-items/bom-versions/', { params });
  return data.data || data.results || data || [];
}

export async function fetchCustomerContacts(params = {}) {
  const { data } = await client.get('/customers/contacts/', { params });
  return data;
}

export async function pinJob(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/pin/`);
  return data.data;
}

export async function fetchSuggestedOrder() {
  const { data } = await client.get('/jobs/suggested-order/');
  return data.data;
}

export async function applySuggestedOrder() {
  // Use new AI-driven queue ordering
  const { data } = await client.post('/scheduling/ai-suggest-order/', { dry_run: false });
  return data.data;
}

// Legacy endpoint (kept for reference, not used)
export async function applySuggestedOrderLegacy() {
  const { data } = await client.post('/jobs/apply-suggested-order/');
  return data.data;
}

export async function dismissJobFlag(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/dismiss-flag/`);
  return data.data;
}

export async function printBom(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/print-bom/`);
  return data.data;
}

// ---------------------------------------------------------------------------
// Design Phase API
// ---------------------------------------------------------------------------

export async function fetchDesignPhases(jobId) {
  const { data } = await client.get('/jobs/design-phases/', { params: { job: jobId } });
  return data.data || data.results || [];
}

export async function createDesignPhase(payload) {
  const { data } = await client.post('/jobs/design-phases/', payload);
  return data.data;
}

export async function updateDesignPhase(phaseId, payload) {
  const { data } = await client.patch(`/jobs/design-phases/${phaseId}/`, payload);
  return data.data;
}

export async function completeDesignPhase(phaseId) {
  const { data } = await client.post(`/jobs/design-phases/${phaseId}/complete/`);
  return data.data;
}

export async function deleteDesignPhase(phaseId) {
  await client.delete(`/jobs/design-phases/${phaseId}/`);
}

export async function completeDesignJob(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/complete-design/`);
  return data.data;
}

export async function pushBomToJob(jobId) {
  const { data } = await client.post(`/jobs/${jobId}/push-bom/`);
  // Return full response so caller can access _push_summary
  return data;
}

// ---------------------------------------------------------------------------
// DNV Process API
// ---------------------------------------------------------------------------

export async function fetchDnvProcesses(jobId) {
  const { data } = await client.get('/jobs/dnv-processes/', { params: { job: jobId } });
  return data.data || data.results || [];
}

export async function createDnvProcess(payload) {
  const { data } = await client.post('/jobs/dnv-processes/', payload);
  return data.data;
}

export async function updateDnvProcess(processId, payload) {
  const { data } = await client.patch(`/jobs/dnv-processes/${processId}/`, payload);
  return data.data;
}

export async function completeDnvProcess(processId) {
  const { data } = await client.post(`/jobs/dnv-processes/${processId}/complete/`);
  return data.data;
}

export async function toggleDnvProcess(processId, isRequired) {
  const { data } = await client.post(`/jobs/dnv-processes/${processId}/toggle/`, { is_required: isRequired });
  return data.data;
}
